import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";

export default function AdminContact() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  if (!draft) return null;
  const contact = draft.contact || {};
  const admissionContacts = draft.admissionContacts || [];

  const setAdmission = (i, k, v) => {
    const next = [...admissionContacts];
    next[i] = { ...next[i], [k]: v };
    patchPath("admissionContacts", next);
  };

  return (
    <div data-testid="cms-contact-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Get in Touch</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Contact & Footer</h1>
        <p className="text-slate-500 mt-2">Address, phones, email, WhatsApp, social links and admission contacts.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Label className="text-slate-700 text-sm">Address</Label>
            <Textarea value={contact.address || ""} onChange={(e) => patchPath("contact.address", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" data-testid="contact-address" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Office Phone</Label>
            <Input value={contact.officePhone || ""} onChange={(e) => patchPath("contact.officePhone", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" data-testid="contact-office-phone-input" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">WhatsApp Number (digits with country code, e.g. 919416426826)</Label>
            <Input value={contact.whatsappPhone || ""} onChange={(e) => patchPath("contact.whatsappPhone", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" data-testid="contact-whatsapp-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-700 text-sm">Default WhatsApp Message</Label>
            <Input value={contact.whatsappMessage || ""} onChange={(e) => patchPath("contact.whatsappMessage", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Email</Label>
            <Input value={contact.email || ""} onChange={(e) => patchPath("contact.email", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" data-testid="contact-email-input" />
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Social Links</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { k: "instagram", label: "Instagram" },
              { k: "linkedin", label: "LinkedIn" },
              { k: "facebook", label: "Facebook" },
              { k: "youtube", label: "YouTube" },
            ].map((s) => (
              <div key={s.k}>
                <Label className="text-slate-700 text-sm">{s.label} URL</Label>
                <Input value={contact[s.k] || ""} onChange={(e) => patchPath(`contact.${s.k}`, e.target.value)} placeholder={`https://${s.k}.com/...`} className="mt-1.5 h-11 rounded-xl border-slate-200" />
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admission Contacts</p>
            <Button variant="outline" size="sm" onClick={() => patchPath("admissionContacts", [...admissionContacts, { role: "New Role", name: "", phone: "" }])} className="rounded-full h-9">
              <Plus className="w-4 h-4 mr-1.5" /> Add
            </Button>
          </div>
          <div className="space-y-3">
            {admissionContacts.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 p-3">
                <Input value={c.role || ""} onChange={(e) => setAdmission(i, "role", e.target.value)} placeholder="Role" className="col-span-4 h-10 rounded-lg border-slate-200" />
                <Input value={c.name || ""} onChange={(e) => setAdmission(i, "name", e.target.value)} placeholder="Name" className="col-span-4 h-10 rounded-lg border-slate-200" />
                <Input value={c.phone || ""} onChange={(e) => setAdmission(i, "phone", e.target.value)} placeholder="Phone" className="col-span-3 h-10 rounded-lg border-slate-200" />
                <button className="col-span-1 text-rose-500 p-2 justify-self-end" onClick={() => patchPath("admissionContacts", admissionContacts.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
